import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Divider, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { except } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { VideoLibraryItem } from "../../../api/types/videoLibraryTypes";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import VerticalNavigationMenuItem from "../../common/navigation/VerticalNavigationMenuItem";
import VerticalNavigationSection from "../../common/navigation/VerticalNavigationSection";
import VerticalNavigationWrapper from "../../common/navigation/VerticalNavigationWrapper";
import AddVideoDialog from "./AddVideoDialog";
import DeleteVideoDialog from "./DeleteVideoDialog";
import EditVideoDialog from "./EditVideoDialog";
import VideoLibraryContainer from "./VideoLibraryContainer";

const getCategoryCounters = (videos: VideoLibraryItem[]): Record<string, number> => {
  const counters: Record<string, number> = {};

  for (const video of videos) {
    for (const categoryId of video.categoryIds) {
      counters[categoryId] = (counters[categoryId] || 0) + 1;
    }
  }

  return counters;
};

interface DialogState {
  openDialog?: "add" | "edit" | "delete";
  videoInfo?: VideoLibraryItem;
}

const VideoLibraryPage = () => {
  usePageTitle("Video Library");

  const { isEntriliaUser } = useUserContext();
  const { hasPermissions } = useClientContext();

  const [categories, fetchCategoriesError] = useFetch(adminApi.getVideoLibraryCategories);
  const [videos, fetchVideosError, { setData: setVideos }] = useFetch(adminApi.getVideosFromLibrary);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogState, setDialogState] = useState<DialogState>({});

  const categoryCounters = useMemo(() => getCategoryCounters(videos ?? []), [videos]);

  const filteredVideos = useMemo(() => {
    return (videos ?? []).filter(
      (video) =>
        (video.name.toLowerCase().includes(searchTerm) || video.description.toLowerCase().includes(searchTerm)) &&
        (selectedCategoryId === "" || video.categoryIds.includes(selectedCategoryId))
    );
  }, [videos, searchTerm, selectedCategoryId]);

  const fetchError = fetchCategoriesError || fetchVideosError;
  if (fetchError) {
    logError(fetchError, "[VideoLibraryPage] fetch");
    return <DataLoadingFailed />;
  }

  if (categories === undefined || videos === undefined) {
    return <InlineLoader />;
  }

  const hasPermissionsToManageVideos = isEntriliaUser && hasPermissions(["ManageOrganizationVideoLibrary"]);

  const handleSelectedCategoryChange = (categoryId: string) => () => {
    setSelectedCategoryId(categoryId);
  };

  const handleVideoAdded = (newItem: VideoLibraryItem) => {
    setVideos((prev) => [newItem, ...(prev ?? [])]);
  };

  const handleVideoUpdated = (updatedItem: VideoLibraryItem) => {
    setVideos((prev) => prev?.map((video) => (video.id === updatedItem.id ? updatedItem : video)) ?? []);
  };

  const handleVideoDeleted = (id: string) => {
    setVideos((prev) => prev?.filter((video) => video.id !== id) ?? []);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLowerCase());
  };

  const handleEditVideo = (videoInfo: VideoLibraryItem) => {
    setDialogState({ openDialog: "edit", videoInfo });
  };

  const handleDeleteVideo = (videoInfo: VideoLibraryItem) => {
    setDialogState({ openDialog: "delete", videoInfo });
  };

  return (
    <>
      <GeneralPageHeader title="Video Library">
        {hasPermissionsToManageVideos && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogState({ openDialog: "add" })}>
            Add New
          </Button>
        )}
      </GeneralPageHeader>
      <Box sx={{ display: "flex", flex: 1 }}>
        <VerticalNavigationWrapper width="21rem">
          <VerticalNavigationSection>
            <VerticalNavigationMenuItem
              text="All Videos"
              badgeCount={videos.length}
              selected={selectedCategoryId === ""}
              onClick={handleSelectedCategoryChange("")}
            />
            {categories.map((category) => (
              <VerticalNavigationMenuItem
                key={category.id}
                text={category.name}
                badgeCount={categoryCounters[category.id] ?? 0}
                selected={selectedCategoryId === category.id}
                onClick={handleSelectedCategoryChange(category.id)}
              />
            ))}
          </VerticalNavigationSection>
        </VerticalNavigationWrapper>

        <Divider flexItem orientation="vertical" />

        <Stack spacing={2.5} sx={{ p: 2.5, flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <RecordCounter totalRecords={videos.length} records={filteredVideos.length} />
            <SearchField onSearch={handleSearch} />
          </Box>

          <VideoLibraryContainer
            items={filteredVideos}
            allCategories={categories}
            hasPermissionsToManageVideos={hasPermissionsToManageVideos}
            onEditVideo={handleEditVideo}
            onDeleteVideo={handleDeleteVideo}
          />
        </Stack>
      </Box>
      {dialogState.openDialog === "add" && (
        <AddVideoDialog
          allCategories={categories}
          existingVideoNames={videos.map((video) => video.name.trim())}
          onAdded={handleVideoAdded}
          onClose={() => setDialogState({})}
        />
      )}
      {dialogState.openDialog === "edit" && dialogState.videoInfo && (
        <EditVideoDialog
          videoInfo={dialogState.videoInfo}
          allCategories={categories}
          existingVideoNames={except(
            videos.map((video) => video.name.trim()),
            [dialogState.videoInfo.name.trim()]
          )}
          onClose={() => setDialogState({})}
          onUpdated={handleVideoUpdated}
        />
      )}
      {dialogState.openDialog === "delete" && dialogState.videoInfo && (
        <DeleteVideoDialog
          videoInfo={dialogState.videoInfo}
          open
          onClose={() => setDialogState({})}
          onDeleted={handleVideoDeleted}
        />
      )}
    </>
  );
};

export default VideoLibraryPage;
